import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

export interface AgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const MODAL_TITLE = 'إقرار وتعهد استخدام المنصة';

const MODAL_CONTENT = `أهلاً بك في Sooq | سوق، المنصة الأردنية الرائدة للإعلانات والخدمات التجارية. لضمان تجربة آمنة وموثوقة للجميع، يرجى تأكيد التزامك بالآتي:

المصداقية: الالتزام بدقة المعلومات والبيانات الواردة في الإعلانات المنشورة.

الأمان الرقمي: جميع تعاملاتك محمية بأعلى معايير التشفير العالمي (SSL) وتخضع لسياسات أمن المعلومات المعتمدة.

التشريعات: الالتزام بكافة القوانين والأنظمة النافذة في المملكة الأردنية الهاشمية والمتعلقة بالمعاملات الإلكترونية.

إن استمرارك في استخدام المنصة يعني ثقتك في بيئتنا الرقمية الآمنة وحرصك على حقوق كافة الأطراف.`;

const CHECKBOX_LABEL =
  'أقر بأنني قرأت شروط الاستخدام وسياسة الخصوصية وأوافق عليها بالكامل.';

/**
 * Mandatory agreement modal shown before Login or Register.
 * Continue button stays disabled until the user checks the agreement checkbox.
 * Glassmorphism backdrop; responsive with safe margins on mobile; Safari-friendly scroll.
 */
const AgreementModal: React.FC<AgreementModalProps> = ({
  isOpen,
  onClose,
  onContinue,
}) => {
  const [checked, setChecked] = useState(false);

  const handleContinue = useCallback(() => {
    if (!checked) return;
    setChecked(false);
    onContinue();
  }, [checked, onContinue]);

  const handleClose = useCallback(() => {
    setChecked(false);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-modal-title"
    >
      {/* Glassmorphism backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal panel: max height with comfortable margins, especially on iPhone */}
      <div
        className="relative w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border border-slate-600/30 bg-slate-100 dark:bg-slate-800/95 text-right overflow-hidden"
        style={{
          WebkitOverflowScrolling: 'touch',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 px-4 pt-4 pb-2 border-b border-slate-600/40">
          <h2
            id="agreement-modal-title"
            className="text-xl font-bold text-slate-800 dark:text-white"
          >
            {MODAL_TITLE}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
            aria-label="إغلاق"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content - Safari smooth scroll */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 min-h-0"
          style={{
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <p className="text-slate-600 dark:text-slate-200 leading-relaxed whitespace-pre-line">
            {MODAL_CONTENT}
          </p>
          <p className="mt-4 text-sm text-slate-400">
            للاطلاع الكامل، راجع{' '}
            <Link
              to="/terms-of-service"
              className="text-primary-400 hover:text-primary-300 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              شروط الاستخدام
            </Link>{' '}
            و{' '}
            <Link
              to="/privacy-policy"
              className="text-primary-400 hover:text-primary-300 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              سياسة الخصوصية
            </Link>
            .
          </p>
        </div>

        {/* Checkbox */}
        <div className="shrink-0 px-4 py-3 border-t border-slate-600/40">
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-slate-500 bg-slate-700 text-primary-500 focus:ring-primary-500 focus:ring-offset-slate-800 cursor-pointer"
              aria-describedby="checkbox-desc"
            />
            <span
              id="checkbox-desc"
              className="text-slate-600 dark:text-slate-200 text-sm leading-relaxed group-hover:text-slate-900 dark:group-hover:text-slate-100"
            >
              {CHECKBOX_LABEL}
            </span>
          </label>
        </div>

        {/* Actions */}
        <div className="shrink-0 flex gap-3 px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-3 px-4 rounded-xl border border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors font-medium"
          >
            إلغاء
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!checked}
            className="flex-1 py-3 px-4 rounded-xl bg-primary-600 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-500 disabled:hover:bg-primary-600"
          >
            المتابعة
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;
